import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import { Alert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import { AlertCard } from '@/experiences/sales-portal/domain/insights/components/alert-card/alert-card';
import { createStyles } from '@/contexts/theme-context';
import { FlatList, Pressable } from 'react-native';
import { hiddenAlertActions } from '@/experiences/sales-portal/domain/insights/components/hidden-alert-actions';
import { Typography } from '@usepepper/sprout-kit-ui';
import { UseAlertPressResult } from '@/experiences/sales-portal/domain/insights/blocs/hooks/use-alert-press';

const PAGE_SIZE = 5;

export interface HiddenAlertsListProps extends UseAlertPressResult {
  alerts: Alert[];
  onDelete: (alert: Alert) => void;
  onRestore: (alert: Alert) => void;
}

export const HiddenAlertsList: React.FC<HiddenAlertsListProps> = ({
  alerts,
  onAccountDetailPress,
  onAlertPress,
  onDelete,
  onRestore,
}) => {
  const [styles, theme] = useStyles();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const visibleAlerts = alerts.slice(0, visibleCount);
  const hasMore = visibleCount < alerts.length;

  return (
    <>
      <FlatList
        contentContainerStyle={styles.alertList}
        data={visibleAlerts}
        keyExtractor={(item) => item.alertUUID}
        renderItem={({ item }) => (
          <AlertCard
            actions={hiddenAlertActions(item, { onDelete, onRestore })}
            alert={item}
            onAlertCardPress={onAccountDetailPress(item)}
            onContentPress={onAlertPress(item)}
          />
        )}
        scrollEnabled={false}
      />

      {hasMore ? (
        <Pressable
          accessibilityRole="button"
          onPress={() => setVisibleCount((count) => count + PAGE_SIZE)}
          style={styles.showMoreButton}
        >
          <Typography color={theme.sk.color.text.tertiary} size="$xs">
            {i18n.t('insights_hub.hidden_alerts.show_more')}
          </Typography>
        </Pressable>
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  alertList: {
    gap: theme.sk.spacing.spaceMedium,
  },
  showMoreButton: {
    alignItems: 'center',
    alignSelf: 'center',
    backgroundColor: theme.sk.color.bg.primary.disabled,
    borderRadius: theme.sk.radii.radiusRound,
    marginBottom: theme.sk.spacing.spaceSmall,
    marginTop: theme.sk.spacing.spaceMedium,
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceXsmall,
  },
}));
